import { Fragment, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { ROLE_SLUGS } from '../../utils/auth/permissionConstants'
import MpesaFeeVarianceTab from './MpesaFeeVarianceTab'
import ReconciliationTab from './ReconciliationTab'
import {
    getReconciliationRequiredTopUps,
    reconcileTopUp,
    getReconciliationRequiredPayouts,
    reconcilePayout,
} from '../../services/LedgerService'
import {
    getReconciliationRequiredCapitalInjections,
    reconcileCapitalInjection,
} from '../../services/CapitalInjectionService'
import {
    getReconciliationRequiredReversals,
    reconcileReversal,
} from '../../services/ReversalService'

const TABS = {
    TOP_UPS:              'top-ups',
    PAYOUTS:              'payouts',
    CAPITAL_INJECTIONS:   'capital-injections',
    REVERSALS:            'reversals',
    MPESA_FEE_VARIANCES:  'mpesa-fee-variances',
}

const formatAmount = (amount) =>
    `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`

const formatDate = (dateString) =>
    new Date(dateString).toLocaleString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })

const AmountCell = ({ amount }) =>
    amount != null
        ? formatAmount(amount)
        : <span className="text-muted fst-italic">Unknown</span>

const PersonCell = ({ person }) => (
    <>
        {person?.name ?? 'Unknown'}
        {person?.is_deleted && (
            <span className="text-muted ms-1" style={{ fontSize: '0.75rem' }}>(deleted)</span>
        )}
    </>
)

const RECEIPT_FIELD = {
    name: 'receipt_number', label: 'M-PESA Receipt Number', type: 'text',
    placeholder: 'e.g. RGX12345678', maxLength: 20,
}

const TRANSACTION_DATE_FIELD = {
    name: 'transaction_date', label: 'Transaction Date & Time', type: 'datetime-local', step: '1',
}

const Reconciliation = () => {
    const userRole = useSelector((state) => state.auth.authUser?.role_slug)

    const [searchParams, setSearchParams] = useSearchParams()
    const initialTab = Object.values(TABS).includes(searchParams.get('tab'))
        ? searchParams.get('tab')
        : TABS.TOP_UPS

    const [activeTab, setActiveTab] = useState(initialTab)

    const switchTab = (tab) => {
        setActiveTab(tab)
        setSearchParams((prev) => {
            const next = new URLSearchParams(prev)
            next.set('tab', tab)
            return next
        }, { replace: true })
    }

    if (userRole !== ROLE_SLUGS.EXEC_ADMIN) {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-body text-center py-5">
                            <p className="text-muted mb-0">You do not have permission to view this page.</p>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <h4 className="card-title mb-0">Reconciliation</h4>
                        </div>
                        <div className="card-body p-0 p-sm-3">
                            <p className="text-muted small mb-3 px-3 px-sm-0 pt-3 pt-sm-0">
                                Items awaiting a manual decision. Pick a tab for its context.
                            </p>

                            <ul className="nav nav-tabs mb-3">
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.TOP_UPS ? 'active' : ''}`}
                                        onClick={() => switchTab(TABS.TOP_UPS)}
                                    >
                                        Vault Top-Ups
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.PAYOUTS ? 'active' : ''}`}
                                        onClick={() => switchTab(TABS.PAYOUTS)}
                                    >
                                        Vendor Payouts
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.CAPITAL_INJECTIONS ? 'active' : ''}`}
                                        onClick={() => switchTab(TABS.CAPITAL_INJECTIONS)}
                                    >
                                        Capital Injections
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.REVERSALS ? 'active' : ''}`}
                                        onClick={() => switchTab(TABS.REVERSALS)}
                                    >
                                        Reversals
                                    </button>
                                </li>
                                <li className="nav-item">
                                    <button
                                        className={`nav-link ${activeTab === TABS.MPESA_FEE_VARIANCES ? 'active' : ''}`}
                                        onClick={() => switchTab(TABS.MPESA_FEE_VARIANCES)}
                                    >
                                        M-PESA Fee Variances
                                    </button>
                                </li>
                            </ul>

                            {activeTab === TABS.TOP_UPS && (
                                <ReconciliationTab
                                    queryKey="reconciliation-required-top-ups"
                                    queryFn={getReconciliationRequiredTopUps}
                                    mutationFn={reconcileTopUp}
                                    idAccessor={(row) => row.id}
                                    noun="top-up"
                                    modalTitle="Reconcile Top-Up"
                                    successMessage="Top-up reconciled successfully. Ledger entries have been written."
                                    emptyMessage="No top-ups require reconciliation."
                                    columns={[
                                        { header: 'Partner',   render: (r) => r.partner_name ?? 'Unknown' },
                                        { header: 'Amount',    className: 'text-nowrap', render: (r) => <AmountCell amount={r.amount} /> },
                                        { header: 'Initiated', className: 'text-nowrap', render: (r) => formatDate(r.created_at) },
                                    ]}
                                    mobileCard={(r) => (
                                        <>
                                            <div className="fw-semibold">{r.partner_name ?? 'Unknown'}</div>
                                            <div className="small mt-1"><AmountCell amount={r.amount} /></div>
                                            <div className="small text-muted mt-1">
                                                <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                {formatDate(r.created_at)}
                                            </div>
                                        </>
                                    )}
                                    fields={[
                                        RECEIPT_FIELD,
                                        {
                                            name: 'source_phone_number', label: 'Source Phone Number', type: 'tel',
                                            placeholder: 'e.g. 254712345678', pattern: '^254\\d{9}$',
                                            help: 'Safaricom format starting with 254 (12 digits).',
                                        },
                                        TRANSACTION_DATE_FIELD,
                                        {
                                            name: 'amount', label: 'Amount (KES)', type: 'number',
                                            placeholder: 'e.g. 10000', min: '1', step: '0.01',
                                            help: 'Confirm the exact amount shown in the Safaricom Business Manager portal.',
                                        },
                                    ]}
                                    seedForm={(row, base) => ({ ...base, amount: row.amount ?? '' })}
                                    describeRow={(row) => (
                                        <p className="text-muted mb-3">
                                            Enter the details from the Safaricom Business Manager portal for the{' '}
                                            <strong>{row.partner_name}</strong> top-up initiated on {formatDate(row.created_at)}.
                                        </p>
                                    )}
                                />
                            )}

                            {activeTab === TABS.PAYOUTS && (
                                <ReconciliationTab
                                    queryKey="reconciliation-required-payouts"
                                    queryFn={getReconciliationRequiredPayouts}
                                    mutationFn={reconcilePayout}
                                    idAccessor={(row) => row.id}
                                    noun="payout"
                                    modalTitle="Reconcile Payout"
                                    successMessage="Payout reconciled successfully. Ledger entries have been written."
                                    emptyMessage="No payouts require reconciliation."
                                    columns={[
                                        { header: 'User',      render: (r) => <PersonCell person={r.user} /> },
                                        { header: 'Recipient', render: (r) => r.receiver_party ?? 'Unknown' },
                                        { header: 'Amount',    className: 'text-nowrap', render: (r) => <AmountCell amount={r.amount} /> },
                                        { header: 'Initiated', className: 'text-nowrap', render: (r) => formatDate(r.created_at) },
                                    ]}
                                    mobileCard={(r) => (
                                        <>
                                            <div className="fw-semibold"><PersonCell person={r.user} /></div>
                                            <div className="small text-muted">To {r.receiver_party ?? 'Unknown'}</div>
                                            <div className="small mt-1"><AmountCell amount={r.amount} /></div>
                                            <div className="small text-muted mt-1">
                                                <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                {formatDate(r.created_at)}
                                            </div>
                                        </>
                                    )}
                                    fields={[RECEIPT_FIELD, TRANSACTION_DATE_FIELD]}
                                    describeRow={(row) => (
                                        <p className="text-muted mb-3">
                                            Enter the details from the Safaricom Business Manager portal for the payout to{' '}
                                            <strong>{row.user?.name ?? row.receiver_party ?? 'this vendor'}</strong> initiated on {formatDate(row.created_at)}.
                                        </p>
                                    )}
                                />
                            )}

                            {activeTab === TABS.CAPITAL_INJECTIONS && (
                                <ReconciliationTab
                                    queryKey="reconciliation-required-capital-injections"
                                    queryFn={getReconciliationRequiredCapitalInjections}
                                    mutationFn={reconcileCapitalInjection}
                                    idAccessor={(row) => row.id}
                                    noun="capital injection"
                                    modalTitle="Reconcile Capital Injection"
                                    successMessage="Capital injection reconciled successfully. Ledger entries have been written."
                                    emptyMessage="No capital injections require reconciliation."
                                    columns={[
                                        { header: 'Injected by', render: (r) => <PersonCell person={r.injected_by} /> },
                                        { header: 'Amount',      className: 'text-nowrap', render: (r) => <AmountCell amount={r.amount} /> },
                                        { header: 'Initiated',   className: 'text-nowrap', render: (r) => formatDate(r.created_at) },
                                    ]}
                                    mobileCard={(r) => (
                                        <>
                                            <div className="fw-semibold"><AmountCell amount={r.amount} /></div>
                                            <div className="small text-muted">By <PersonCell person={r.injected_by} /></div>
                                            <div className="small text-muted mt-1">
                                                <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                {formatDate(r.created_at)}
                                            </div>
                                        </>
                                    )}
                                    fields={[
                                        RECEIPT_FIELD,
                                        {
                                            name: 'source_phone_number', label: 'Source Phone Number', type: 'tel',
                                            placeholder: 'e.g. 254712345678', pattern: '^254\\d{9}$',
                                            help: 'Safaricom format starting with 254 (12 digits). Correct here if the actual charge came from a different number than the one used to initiate.',
                                        },
                                        TRANSACTION_DATE_FIELD,
                                        {
                                            name: 'amount', label: 'Amount (KES)', type: 'number',
                                            placeholder: 'e.g. 10000', min: '1', step: '0.01',
                                            help: 'Confirm the exact amount shown in the Safaricom Business Manager portal. Correct here if it differs from what was initiated.',
                                        },
                                    ]}
                                    seedForm={(row, base) => ({
                                        ...base,
                                        amount: row.amount ?? '',
                                    })}
                                    describeRow={(row) => (
                                        <p className="text-muted mb-3">
                                            Enter the details from the Safaricom Business Manager portal for the capital injection initiated on {formatDate(row.created_at)}.
                                        </p>
                                    )}
                                />
                            )}

                            {activeTab === TABS.REVERSALS && (
                                <ReconciliationTab
                                    queryKey="reconciliation-required-reversals"
                                    queryFn={getReconciliationRequiredReversals}
                                    mutationFn={reconcileReversal}
                                    idAccessor={(row) => row.id}
                                    noun="reversal"
                                    modalTitle="Reconcile Reversal"
                                    successMessage="Reversal reconciled successfully. Ledger entries have been written."
                                    emptyMessage="No reversals require reconciliation."
                                    columns={[
                                        { header: 'Initiated by',     render: (r) => <PersonCell person={r.initiated_by} /> },
                                        { header: 'Original Receipt', render: (r) => r.mpesa_receipt_number },
                                        { header: 'Amount',           className: 'text-nowrap', render: (r) => <AmountCell amount={r.amount} /> },
                                        { header: 'Initiated',        className: 'text-nowrap', render: (r) => formatDate(r.created_at) },
                                    ]}
                                    mobileCard={(r) => (
                                        <>
                                            <div className="fw-semibold"><AmountCell amount={r.amount} /></div>
                                            <div className="small text-muted">Receipt {r.mpesa_receipt_number}</div>
                                            <div className="small text-muted">By <PersonCell person={r.initiated_by} /></div>
                                            <div className="small text-muted mt-1">
                                                <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                {formatDate(r.created_at)}
                                            </div>
                                        </>
                                    )}
                                    fields={[
                                        {
                                            name: 'transaction_id', label: 'Reversal Transaction ID', type: 'text',
                                            placeholder: 'e.g. RGX12345678', maxLength: 20,
                                            help: 'The M-PESA transaction ID for the reversal itself, not the original transaction.',
                                        },
                                        { ...TRANSACTION_DATE_FIELD, label: 'Reversal Date & Time' },
                                    ]}
                                    describeRow={(row) => (
                                        <p className="text-muted mb-3">
                                            Enter the details from the Safaricom Business Manager portal for the reversal of receipt{' '}
                                            <strong>{row.mpesa_receipt_number}</strong> initiated on {formatDate(row.created_at)}.
                                        </p>
                                    )}
                                />
                            )}

                            {activeTab === TABS.MPESA_FEE_VARIANCES && (
                                <MpesaFeeVarianceTab />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    )
}

export default Reconciliation
